import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { useAuth } from "../../../../context/AuthContext";
import { producaoService } from "../../../../services/producaoService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { UserStatsMontagem, DailyStatsMontagem } from "../types";
import { parseHHMMSSToSeconds } from "../../Producao/utils/formatTime";
import { useFilaIntegracaoEvents } from "../../../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";

interface RankingMontagemContextData {
  montadores: UserStatsMontagem[];
  dailyStats: DailyStatsMontagem;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  dateLabel: string;
  selectedLojaNome?: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

interface RankingMontagemProviderProps {
  children: React.ReactNode;
}

const RankingMontagemContext = createContext<RankingMontagemContextData>(
  {} as RankingMontagemContextData
);

export const RankingMontagemProvider: React.FC<
  RankingMontagemProviderProps
> = ({ children }) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);

  const getTodayDate = useCallback(() => {
    const now = BrazilDate.now();
    const timezone = BrazilDate.getTimezone();
    return format(now, "dd/MM/yyyy", { timeZone: timezone });
  }, []);

  const dateLabel = getTodayDate();

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const rankingDiaFilters = useMemo(
    () => ({ lojaId: selectedLojaId ?? null }),
    [selectedLojaId]
  );

  useFilaIntegracaoEvents(selectedLojaId ?? null, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.rankingMontagem,
  });

  const { data: rankingDiaData, isLoading: isLoadingRankingDia } = useQuery({
    queryKey: ["ranking-dia-montagem", rankingDiaFilters],
    queryFn: () => producaoService.getRankingDiaMontagem(rankingDiaFilters),
    refetchInterval: false,
  });

  const isLoading = isLoadingLojas || isLoadingRankingDia;

  const montadores: UserStatsMontagem[] = useMemo(() => {
    if (!rankingDiaData?.ranking) return [];

    return rankingDiaData.ranking.map((item) => ({
      nome: item.nome.split(" ")[0] || "",
      sobrenome: item.nome.split(" ").slice(1).join(" ") || "",
      cargo: "montagem",
      producao: item.quantidadePizzas,
      tempoMedio: parseHHMMSSToSeconds(item.tempoMedio),
    }));
  }, [rankingDiaData]);

  const dailyStats: DailyStatsMontagem = useMemo(() => {
    return {
      pizzasFinalizadas: rankingDiaData?.quantidadeTotal ?? 0,
      tempoMedioGeral: parseHHMMSSToSeconds(
        rankingDiaData?.tempoMedioGeral ?? "00:00:00"
      ),
      pizzaiolos: rankingDiaData?.ranking?.length ?? 0,
      emProducao: rankingDiaData?.quantidadeEmMontagem ?? 0,
      pendentesFila: rankingDiaData?.pendentesFila ?? 0,
    };
  }, [rankingDiaData]);

  const selectedLojaNome = useMemo(
    () => lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  return (
    <RankingMontagemContext.Provider
      value={{
        montadores,
        dailyStats,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        dateLabel,
        selectedLojaNome,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingMontagemContext.Provider>
  );
};

export const useRankingMontagem = (): RankingMontagemContextData => {
  const context = useContext(RankingMontagemContext);
  if (!context) {
    throw new Error(
      "useRankingMontagem must be used within RankingMontagemProvider"
    );
  }
  return context;
};
